import { BuildingOfficeIcon, HeartIcon, BoltIcon, UserGroupIcon, ShieldCheckIcon, GlobeAltIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PartnerTypesSection = () => {
  const partnerTypes = [
    {
      icon: BuildingOfficeIcon,
      title: "Healthcare Providers",
      description: "Hospitals, clinics, and medical practices looking to support patients aging at home safely",
      benefits: [
        "Patient engagement tools to reduce readmissions", 
        "Post-discharge monitoring and support",
        "Smart fall risk assessments for home environments", 
        "Family communication and care coordination",
        "Integration with existing EMR systems",
        "Remote wellness monitoring capabilities"
      ],
      examples: "Hospitals, Primary Care Clinics, Geriatricians, Physical Therapy Practices"
    },
    {
      icon: HeartIcon,
      title: "Home Care Agencies",
      description: "Professional caregiving services wanting to enhance client offerings and differentiate their services",
      benefits: [
        "Multi-client management dashboard",
        "Real-time family communication tools", 
        "Automated care plan tracking and reporting",
        "Staff coordination and scheduling tools",
        "Service differentiation through technology",
        "Client outcome tracking and analytics"
      ],
      examples: "Home Care Agencies, Private Duty Nurses, Companion Care Services, Medical Home Care"
    },
    {
      icon: UserGroupIcon,
      title: "Senior Living Communities", 
      description: "Independent living, assisted living, and CCRC providers supporting aging in place transitions",
      benefits: [
        "Resident support tools for independent living",
        "Family engagement and communication platforms",
        "Transition planning for different care levels", 
        "Community outreach and wellness programs",
        "Waitlist management and pre-move preparation",
        "Alumni support for aging in place graduates"
      ],
      examples: "Independent Living Communities, CCRCs, Assisted Living Facilities, Active Adult Communities"
    },
    {
      icon: BoltIcon,
      title: "Technology Partners",
      description: "Tech companies with complementary solutions looking to enhance the senior care ecosystem",
      benefits: [
        "Seamless API integrations and data sharing",
        "Cross-platform compatibility and support",
        "Joint product development opportunities", 
        "Shared go-to-market strategies",
        "Technical partnership and co-development",
        "Integration marketplace opportunities"
      ],
      examples: "Health Tech Companies, Smart Home Providers, Telehealth Platforms, Wearable Device Makers"
    },
    {
      icon: ShieldCheckIcon,
      title: "Insurance & Financial Services",
      description: "Insurance providers and financial advisors looking to add value for senior clients",
      benefits: [
        "Risk reduction tools to lower claims",
        "Value-added services for policyholders",
        "Client engagement and retention tools",
        "Preventive care support programs",
        "Family financial planning integration",
        "Claims prevention and early intervention"
      ],
      examples: "Health Insurance Plans, Life Insurance Companies, Financial Advisors, Long-term Care Insurers"
    },
    {
      icon: GlobeAltIcon,
      title: "Community Organizations",
      description: "Non-profits, government agencies, and community groups supporting local seniors",
      benefits: [
        "Community-wide senior safety initiatives",
        "Grant-funded program implementation",
        "Volunteer coordination and training tools",
        "Public health and safety data insights",
        "Resource coordination and referral systems",
        "Social determinants of health tracking"
      ],
      examples: "Area Agencies on Aging, Senior Centers, Churches, Non-profit Organizations, Public Health Departments"
    }
  ];

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Partnership Opportunities</h2>
            <p className="text-xl text-muted-foreground">
              We work with organizations across the senior care ecosystem
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {partnerTypes.map((type, index) => (
              <div key={index} className="glass-card p-8 rounded-2xl hover-scale">
                <div className="flex items-center gap-3 mb-3">
                  <type.icon className="w-8 h-8 text-primary flex-shrink-0" />
                  <h3 className="text-2xl font-bold">{type.title}</h3>
                </div>
                <p className="text-muted-foreground mb-6">{type.description}</p>
                
                <div className="space-y-4 mb-6">
                  <h4 className="font-semibold text-sm uppercase tracking-wide">Partnership Benefits:</h4>
                  <ul className="space-y-2">
                    {type.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="p-4 bg-accent/50 rounded-xl">
                  <p className="text-sm font-medium text-muted-foreground">
                    <strong>Examples:</strong> {type.examples}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerTypesSection;